import { Seo } from "../components/Seo";
import React from "react";
import { Check, ArrowRight } from "@phosphor-icons/react";
import { useContent } from "../context/ContentContext";
import { resolveImg } from "../lib/api";
import { PageHero } from "../components/PageHero";
import { Reveal, Overline } from "../components/Reveal";
import { EnquiryForm } from "../components/EnquiryForm";
import { MemberRegisterForm } from "../components/MemberRegisterForm";
import { ClubApplyForm } from "../components/ClubApplyForm";
import { BENEFITS } from "../data/defaults";

export default function Membership() {
  const { content } = useContent();
  return (
    <div data-testid="membership-page">
      <Seo title="Membership & Clubs — Join Te Manawa Table Tennis" description="Join Te Manawa as a player or affiliate your club. Register online for a membership number and become part of table tennis in the central North Island." />
      <PageHero overline="Membership & Clubs" title={content.membership_hero_heading} subtitle={content.membership_hero_sub} />

      {/* Intro + image */}
      <section className="bg-cream py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <Overline className="text-navy/60">How to join</Overline>
            <h2 className="font-heading text-4xl sm:text-5xl text-navy font-medium tracking-tight mt-5 leading-[1.05]">
              There's a place for you at the table
            </h2>
            <p className="mt-6 text-lg text-navy/75 leading-relaxed">{content.membership_intro}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              {content.membership_image && <img src={resolveImg(content.membership_image)} alt="Table tennis club" className="w-full aspect-[4/3] object-cover" />}
              <div className="absolute -top-5 -left-5 h-24 w-24 border-4 border-gold hidden sm:block" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Benefits — sticky heading + list */}
      <section className="bg-white py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="md:sticky md:top-28">
              <Reveal>
                <Overline className="text-navy/60">The benefits</Overline>
                <h2 className="font-heading text-4xl sm:text-5xl text-navy font-medium tracking-tight mt-5 leading-[1.02]">Why join Te Manawa?</h2>
              </Reveal>
            </div>
          </div>
          <div className="md:col-span-8 md:col-start-5">
            {BENEFITS.map((b, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="flex items-start gap-5 py-6 border-b border-navy/10">
                  <span className="grid place-items-center h-9 w-9 bg-gold text-navy rounded-full shrink-0 mt-1">
                    <Check size={18} weight="bold" />
                  </span>
                  <p className="font-heading text-xl md:text-2xl text-navy font-medium leading-snug">{b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Affiliation */}
      <section className="relative bg-navy-dark text-white overflow-hidden py-24 md:py-28">
        <div className="absolute inset-0 noise-overlay" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Overline className="text-gold">{content.affiliation_heading}</Overline>
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight mt-5">Bring your club into the whānau</h2>
            <p className="mt-6 text-white/75 text-lg leading-relaxed">{content.affiliation_body}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-white text-navy border border-white/10 p-8 md:p-10 mt-10" id="clubs">
              <h3 className="font-heading text-2xl text-navy font-medium mb-6">Apply to affiliate your club</h3>
              <ClubApplyForm />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Member registration */}
      <section className="bg-cream py-24 md:py-28" id="join">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Reveal className="mb-10">
            <Overline className="text-navy/60">Join Us</Overline>
            <h2 className="font-heading text-4xl sm:text-5xl text-navy font-medium tracking-tight mt-5">Register as a member</h2>
            <p className="mt-4 text-navy/70 text-lg">Sign up to become part of Te Manawa. We'll issue a provisional membership number and confirm your details with Table Tennis New Zealand.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-white border border-navy/10 p-8 md:p-10">
              <MemberRegisterForm />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
